import {
  IonHeader,
  IonToolbar,
  IonContent,
  IonFooter,
  IonButtons,
  IonButton,
  IonPopover,
  IonItem,
  IonRange,
  IonLabel,
  IonListHeader,
  IonCol,
  IonRow,
  IonGrid,
  IonIcon,
  ItemReorderEventDetail,
  IonDatetime,
  IonModal,
} from '@ionic/react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IPHIComponent } from '../../../components/phi/phi.component'
import './timesReorder.component.css'
import { addOutline, removeOutline } from 'ionicons/icons'
import {
  addMinutesToDate,
  getFormattedDate,
  getFormattedTime,
  getMidpointDate,
  getTimeDifferenceInMinutes,
  isValidZollTime,
  subtractMinutesToDate,
  truncateSecondsIfNecessary,
} from '../../../utilities/dateTime.util'
import { IItem } from '../../../components/grid/store/grid.types'
import { useDispatch, useSelector } from 'react-redux'
import { setEditedItem } from '../store/timeline.actions'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { IServiceConfig } from '../../configurations/store/configurations.types'

export interface IPopoverState {
  showPopover: boolean
}

export interface IPopoverData {
  selected: IItem
  start: IItem
  end: IItem
  event?: CustomEvent<ItemReorderEventDetail>
}

interface ITimesReorderProps extends IPHIComponent {
  resetPopoverState: () => void
  popoverState: IPopoverState
  popoverData?: IPopoverData
}

const TimesReorderComponent: React.FC<ITimesReorderProps> = (props: ITimesReorderProps): JSX.Element => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isTablet } = useDeviceMetrics()
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = Boolean(DISPLAY_SECONDS)

  const { resetPopoverState, popoverState, popoverData } = props
  const { showPopover } = popoverState

  const [isCustomInput, setIsCustomInput] = useState<boolean>(false)
  const [addClickCount, setAddClickCount] = useState(0)
  const [subractClickCount, setSubtractClickCount] = useState(0)
  const [value, setValue] = useState(0)
  const [max, setMax] = useState<number>()
  const [selectedTime, setSelectedTime] = useState('')
  const [selectedDate, setSelectedDate] = useState('')
  const [showDateModal, setShowDateModal] = useState(false)
  const [showTimeModal, setShowTimeModal] = useState(false)

  const date = useRef<null | HTMLIonDatetimeElement>(null)
  const time = useRef<null | HTMLIonDatetimeElement>(null)

  const startDate = popoverData?.start?.subHeader
  const startTime = popoverData?.start?.labelHeader
  const endDate = popoverData?.end?.subHeader
  const endTime = popoverData?.end?.labelHeader

  const checkIsCustomInput = () => {
    if (!startDate || !startTime || !endDate || !endTime) return true
    return false
  }

  const updateSelectedDateTime = (dateTime: Date) => {
    const time = getFormattedTime(dateTime)
    const date = getFormattedDate(dateTime)
    setSelectedTime(time)
    setSelectedDate(date)
  }

  const setSelectedDateTime = () => {
    const time = popoverData?.selected?.labelHeader?.trim() || ''
    const date = popoverData?.selected?.subHeader?.trim() || ''
    setSelectedTime(time)
    setSelectedDate(date)
  }

  const getISoDateTime = () => {
    if (selectedDate && selectedTime && isValidZollTime(selectedTime)) {
      const dateTime = `${selectedDate} ${selectedTime}`
      const parts = dateTime.split(/[\/\s:]/)
      const [month, day, year, hour, minute] = parts.map(Number)
      const isoDateTime = new Date(Date.UTC(2000 + year, month - 1, day, hour, minute)).toISOString()
      return isoDateTime
    }
  }

  const getStartDateTime = () => {
    let startDateTime = new Date()
    if (popoverData && startDate) {
      startDateTime = new Date(startDate)
      if (startTime) {
        startDateTime.setMinutes(+startTime.split(':')[1])
        startDateTime.setHours(+startTime.split(':')[0])
      }
    }
    return startDateTime
  }

  const getEndDateTime = () => {
    let endDateTime = new Date()
    if (popoverData && endDate) {
      endDateTime = new Date(endDate)
      if (endTime) {
        endDateTime.setMinutes(+endTime.split(':')[1])
        endDateTime.setHours(+endTime.split(':')[0])
      }
    }
    return endDateTime
  }

  const getSelectedDateTime = () => {
    const dateTime = new Date(selectedDate || 0)
    if (selectedTime) {
      dateTime.setMinutes(+selectedTime.split(':')[1])
      dateTime.setHours(+selectedTime.split(':')[0])
    }
    return dateTime
  }

  useEffect(() => {
    if (showPopover) {
      if (!popoverData || !popoverData.selected) return

      const isCustomInput = checkIsCustomInput()
      setIsCustomInput(isCustomInput)
      if (isCustomInput) {
        setSelectedDateTime()
        return
      }

      const maxMinutesDiff = getTimeDifferenceInMinutes(getStartDateTime(), getEndDateTime())
      setMax(maxMinutesDiff)

      const pinDateTime = getMidpointDate(getStartDateTime(), getEndDateTime())
      updateSelectedDateTime(pinDateTime)

      const minutesDiff = getTimeDifferenceInMinutes(getStartDateTime(), pinDateTime)
      setValue(minutesDiff)
    }
  }, [showPopover])

  const pinFormatter = () => {
    return selectedTime
  }

  const onTimesRangeChange = (e: CustomEvent) => {
    setValue(e.detail.value)
    setAddClickCount(0)
    setSubtractClickCount(0)

    const updatedDateTime = addMinutesToDate(getStartDateTime(), e.detail.value)
    updateSelectedDateTime(updatedDateTime)
  }

  const onAddTimeClick = () => {
    const clickCount = addClickCount + 1

    setSubtractClickCount(0)
    setAddClickCount(clickCount)
    setValue(value + clickCount)

    const updatedDateTime = addMinutesToDate(getSelectedDateTime(), clickCount)
    updateSelectedDateTime(updatedDateTime)
  }

  const onSubtractTimeClick = () => {
    const clickCount = subractClickCount + 1

    setAddClickCount(0)
    setSubtractClickCount(clickCount)
    setValue(value - clickCount)

    const updatedDateTime = subtractMinutesToDate(getSelectedDateTime(), clickCount)
    updateSelectedDateTime(updatedDateTime)
  }

  const getTrigger = () => {
    return isTablet ? `${popoverData?.selected?.key}_trigger` : `${popoverData?.selected?.key}_col`
  }

  const save = () => {
    const selected = popoverData?.selected
    if (selected) {
      selected.date = getSelectedDateTime()
      selected.labelHeader = /\d{2}:\d{2}:\d{2}/.test(selectedTime) ? selectedTime : `${selectedTime}:00`
      selected.subHeader = selectedDate
      dispatch(setEditedItem(selected))
      dismiss()
    } else {
      dismiss()
    }
  }

  const getTimePlaceholder = () => {
    if (selectedTime) return truncateSecondsIfNecessary(selectedTime, displaySeconds)
    if (!selectedTime && displaySeconds) return 'HH:MM:SS'
    return 'HH:MM'
  }

  const customTimeDismiss = () => {
    const value = time?.current?.value
    if (value && typeof value === 'string') {
      const [, timePart] = value.split('T')
      const [hours, minutes] = timePart.split(':').slice(0, 2)
      setSelectedTime(`${hours}:${minutes}:00`)
    }
    setShowTimeModal(false)
  }

  const customDateDismiss = () => {
    const value = date?.current?.value
    if (value && typeof value === 'string') {
      const dateTime = new Date(value)
      const month = ('0' + (dateTime.getMonth() + 1)).slice(-2)
      const day = ('0' + dateTime.getDate()).slice(-2)
      const year = dateTime.getFullYear().toString().slice(-2)

      const date = `${month}/${day}/${year}`
      setSelectedDate(date)
    }
    setShowDateModal(false)
  }

  const dismiss = () => {
    setSelectedDate('')
    setSelectedTime('')
    popoverData?.event?.detail.complete(false)
    resetPopoverState()
  }

  return (
    <>
      {popoverData && (
        <IonPopover
          data-testid='TimesReorderPopover'
          className='times-reorder-popover'
          backdropDismiss={false}
          showBackdrop={popoverState.showPopover}
          isOpen={popoverState.showPopover}
          trigger={getTrigger()}>
          {
            <>
              <IonHeader className='ion-no-border'>
                <IonToolbar color='light'>
                  <IonListHeader color='light'>
                    <IonLabel color='dark' className='reorder-title'>
                      {isCustomInput ? t('timeline.SetEventTime') : t('timeline.ModifyEventTime')}
                    </IonLabel>
                  </IonListHeader>
                </IonToolbar>
              </IonHeader>
              <IonContent scroll-y={false}>
                <IonItem>
                  <IonGrid className='times-reorder-grid'>
                    {!isCustomInput && (
                      <>
                        <IonRow>
                          <IonCol className='times-reorder-col'>
                            <div>
                              <IonLabel color='Primary'>
                                {truncateSecondsIfNecessary(startTime || '', displaySeconds)}
                              </IonLabel>
                            </div>
                            <div>
                              <IonLabel color='Primary'>{startDate}</IonLabel>
                            </div>
                          </IonCol>
                          <IonRange
                            className='reorder-range'
                            aria-label='Times Range'
                            value={value}
                            max={max}
                            pin={true}
                            pinFormatter={pinFormatter}
                            onIonChange={onTimesRangeChange}
                          />
                          <IonCol className='times-reorder-col'>
                            <div>
                              <IonLabel>{truncateSecondsIfNecessary(endTime || '', displaySeconds)}</IonLabel>
                            </div>
                            <div>
                              <IonLabel>{endDate}</IonLabel>
                            </div>
                          </IonCol>
                        </IonRow>
                        <IonRow>
                          <IonCol offset='3.1'>
                            <IonButton
                              className='substract-icon-button'
                              size='small'
                              fill='clear'
                              onClick={onSubtractTimeClick}>
                              <IonIcon
                                color='primary'
                                data-testid='subtractTime'
                                icon={removeOutline}
                                slot='icon-only'
                              />
                            </IonButton>
                          </IonCol>
                          <IonCol>
                            <IonButton className='add-icon-button' size='small' fill='clear' onClick={onAddTimeClick}>
                              <IonIcon color='primary' data-testid='addTime' icon={addOutline} slot='icon-only' />
                            </IonButton>
                          </IonCol>
                        </IonRow>
                      </>
                    )}
                    {isCustomInput && (
                      <>
                        <IonRow>
                          <IonCol class='ion-padding-start'>
                            <IonButton
                              id='timeButton'
                              expand='block'
                              size='default'
                              onClick={() => setShowTimeModal(true)}>
                              <span>{getTimePlaceholder()}</span>
                            </IonButton>
                          </IonCol>
                          <IonCol class='ion-padding-start'>
                            <IonButton
                              id='dateButton'
                              expand='block'
                              size='default'
                              onClick={() => setShowDateModal(true)}>
                              <span>{selectedDate || 'MM:DD:YY'}</span>
                            </IonButton>
                          </IonCol>
                        </IonRow>

                        <IonModal
                          isOpen={showTimeModal}
                          showBackdrop
                          onDidDismiss={customTimeDismiss}
                          keepContentsMounted={true}
                          className='date-time-modal'>
                          <IonDatetime
                            id='date'
                            ref={time}
                            presentation='time'
                            locale='en-US-u-hc-h23'
                            hourCycle='h23'
                            value={getISoDateTime()}></IonDatetime>
                        </IonModal>

                        <IonModal
                          isOpen={showDateModal}
                          showBackdrop
                          onDidDismiss={customDateDismiss}
                          keepContentsMounted={true}
                          className='date-time-modal'>
                          <IonDatetime
                            id='date'
                            showDefaultButtons={true}
                            ref={date}
                            presentation='date'
                            value={getISoDateTime()}></IonDatetime>
                        </IonModal>
                      </>
                    )}
                  </IonGrid>
                </IonItem>
              </IonContent>
              <IonFooter className='ion-no-border'>
                <IonToolbar color='light' className='p-10'>
                  <IonButtons>
                    <>
                      <IonButton
                        data-testid='TimesReorderCancel'
                        className='btn btn-secondary mr-8'
                        expand='block'
                        onClick={dismiss}
                        id='TimesReorder-popover-cancel-button'>
                        {t('common.buttons.Cancel')}
                      </IonButton>
                      <IonButton
                        data-testid='TimesReorderSave'
                        className='btn btn-primary'
                        expand='block'
                        onClick={save}
                        disabled={!selectedDate || !selectedTime || !isValidZollTime(selectedTime)}
                        id='TimesReorder-popover-save-button'>
                        {t('common.buttons.Save')}
                      </IonButton>
                    </>
                  </IonButtons>
                </IonToolbar>
              </IonFooter>
            </>
          }
        </IonPopover>
      )}
    </>
  )
}

export default TimesReorderComponent
